/**
 * Unique shoppers on Shopify product pages. Saves every browser for the life of
 * the listing. Shows who is checking it out now, how many people are looking,
 * and how many carts currently hold this product.
 *
 * Cart state is reported on the same heartbeat as views. Dawn adds to cart with
 * fetch() and then publishes `cart-update` — the page must send `inCart` after
 * that succeeds, or the API keeps the cart count at zero.
 */
(function () {
  if (window.__tgViewerCountLoaded) return;
  window.__tgViewerCountLoaded = true;

  var HEARTBEAT_MS = 20000;
  var STORAGE_KEY = 'tg_viewer_id';
  var lastInCart = null;
  var cartReadGen = 0;

  function scriptEl() {
    return document.currentScript || document.querySelector('script[src*="viewer-count.js"]');
  }

  function apiUrl() {
    var el = scriptEl();
    if (el && el.getAttribute('data-api')) return el.getAttribute('data-api');
    var src = el && el.src ? el.src : '';
    try {
      var u = new URL(src, window.location.href);
      return u.origin + '/api/storefront/viewers';
    } catch (e) {
      return '/api/storefront/viewers';
    }
  }

  function viewerId() {
    try {
      var existing = localStorage.getItem(STORAGE_KEY);
      if (existing && /^[a-zA-Z0-9_-]{8,64}$/.test(existing)) return existing;
      var id =
        (window.crypto && crypto.randomUUID && crypto.randomUUID().replace(/-/g, '')) ||
        'v' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      id = String(id).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
      if (id.length < 8) id = (id + 'xxxxxxxx').slice(0, 8);
      localStorage.setItem(STORAGE_KEY, id);
      return id;
    } catch (e) {
      return 'v' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  }

  function productInfo() {
    var mount = document.getElementById('tg-viewer-count');
    var id =
      (mount && (mount.getAttribute('data-product-id') || mount.dataset.productId)) ||
      (document.querySelector('[data-product-id]') &&
        document.querySelector('[data-product-id]').getAttribute('data-product-id')) ||
      (document.querySelector('input[name="product-id"]') &&
        document.querySelector('input[name="product-id"]').value) ||
      (window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.product &&
        ShopifyAnalytics.meta.product.id) ||
      (window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.page &&
        ShopifyAnalytics.meta.page.resourceType === 'product' &&
        ShopifyAnalytics.meta.page.resourceId);
    var handle =
      (mount && (mount.getAttribute('data-product-handle') || mount.dataset.productHandle)) ||
      (window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.product &&
        ShopifyAnalytics.meta.product.handle) ||
      '';
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (m) handle = decodeURIComponent(m[1]);
    }
    if (id == null || id === '') return null;
    return { productId: String(id).replace(/\D/g, ''), handle: String(handle || '') };
  }

  function itemMatchesProduct(item, productId) {
    if (!item || item.product_id == null) return false;
    return String(item.product_id) === String(productId);
  }

  function cartHasProduct(cart, productId) {
    var id = String(productId || '');
    if (!cart || !id) return false;
    if (itemMatchesProduct(cart, id)) return true;
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (itemMatchesProduct(items[i], id)) return true;
    }
    return false;
  }

  function isCartUrl(url) {
    return /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/i.test(String(url || ''));
  }

  function readCart(cb) {
    var info = productInfo();
    var gen = ++cartReadGen;
    if (!info || !info.productId) {
      if (cb) cb(lastInCart);
      return;
    }
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('cart');
        return res.json();
      })
      .then(function (cart) {
        if (gen !== cartReadGen) return;
        lastInCart = cartHasProduct(cart, info.productId);
        if (cb) cb(lastInCart);
      })
      .catch(function () {
        if (gen !== cartReadGen) return;
        if (cb) cb(lastInCart);
      });
  }

  function ensureMount() {
    var el = document.getElementById('tg-viewer-count');
    if (!el) {
      el = document.createElement('div');
      el.id = 'tg-viewer-count';
      el.hidden = true;
    }
    var form =
      document.querySelector('product-form') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('.product-form') ||
      document.querySelector('.product__info-container');
    if (form && form.parentNode && el.nextSibling !== form) {
      form.parentNode.insertBefore(el, form);
    } else if (!el.parentNode) {
      (document.querySelector('main') || document.body).appendChild(el);
    }
    return el;
  }

  function render(el, data) {
    if (!data || !data.visible || !data.text) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    el.setAttribute('role', 'status');
    el.style.cssText =
      'display:flex;align-items:flex-start;flex-wrap:nowrap;gap:8px;margin:0 0 12px;font-size:13px;line-height:1.4;color:#4b5563;letter-spacing:0.01em;';
    var live = data.mode === 'live';
    var dotColor = live ? '#16a34a' : data.mode === 'cart' ? '#d97706' : '#9ca3af';
    var dotAnim = live
      ? 'box-shadow:0 0 0 0 rgba(22,163,74,.6);animation:tg-viewer-pulse 1.8s ease-out infinite;'
      : 'box-shadow:none;';
    el.innerHTML =
      '<span aria-hidden="true" style="display:block;flex:0 0 auto;width:8px;height:8px;margin-top:calc((1.4em - 8px) / 2);border-radius:999px;background:' +
      dotColor +
      ';' +
      dotAnim +
      '"></span>' +
      '<span style="flex:1;min-width:0;">' +
      String(data.text).replace(/[<>]/g, '') +
      '</span>';
    if (!document.getElementById('tg-viewer-count-style')) {
      var style = document.createElement('style');
      style.id = 'tg-viewer-count-style';
      style.textContent =
        '@keyframes tg-viewer-pulse{0%{box-shadow:0 0 0 0 rgba(22,163,74,.55)}70%{box-shadow:0 0 0 8px rgba(22,163,74,0)}100%{box-shadow:0 0 0 0 rgba(22,163,74,0)}}';
      document.head.appendChild(style);
    }
  }

  var timer = null;
  var inflight = false;
  var pendingPing = false;

  function ping(keepalive) {
    if (document.visibilityState === 'hidden' && !keepalive) return;
    var info = productInfo();
    if (!info || !info.productId) return;
    var mount = ensureMount();
    if (inflight && !keepalive) {
      pendingPing = true;
      return;
    }
    inflight = true;
    pendingPing = false;
    var payload = {
      productId: info.productId,
      handle: info.handle,
      viewerId: viewerId(),
    };
    if (lastInCart === true || lastInCart === false) payload.inCart = lastInCart;
    var body = JSON.stringify(payload);
    var url = apiUrl();
    var done = function (data) {
      inflight = false;
      try {
        render(mount, data);
      } catch (e) {}
      if (pendingPing) {
        pendingPing = false;
        ping(false);
      }
    };
    if (keepalive && navigator.sendBeacon) {
      try {
        var beaconBody = new Blob([body], { type: 'text/plain' });
        if (!navigator.sendBeacon(url, beaconBody)) throw new Error('beacon');
      } catch (e) {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: body,
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
        }).catch(function () {});
      }
      inflight = false;
      if (pendingPing) {
        pendingPing = false;
        ping(false);
      }
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body,
      mode: 'cors',
      credentials: 'omit',
      keepalive: !!keepalive,
    })
      .then(function (res) {
        return res.json();
      })
      .then(done)
      .catch(function () {
        inflight = false;
        if (pendingPing) {
          pendingPing = false;
          ping(false);
        }
      });
  }

  function syncCartAndPing() {
    readCart(function () {
      ping(false);
    });
  }

  function applyCartSnapshot(cart) {
    var info = productInfo();
    if (!info || !info.productId || !cart) {
      syncCartAndPing();
      return;
    }
    if (Array.isArray(cart.items)) {
      cartReadGen++;
      lastInCart = cartHasProduct(cart, info.productId);
      ping(false);
      return;
    }
    if (cart.product_id != null && String(cart.product_id) === String(info.productId) && !cart.status) {
      cartReadGen++;
      lastInCart = true;
      ping(false);
      return;
    }
    syncCartAndPing();
  }

  function start() {
    readCart(function () {
      ping(false);
    });
    if (timer) clearInterval(timer);
    timer = setInterval(function () {
      ping(false);
    }, HEARTBEAT_MS);
  }

  function hookCartEvents() {
    if (window.__tgViewerCountCartHooked) return;
    window.__tgViewerCountCartHooked = true;

    if (typeof subscribe === 'function') {
      subscribe('cart-update', function (event) {
        applyCartSnapshot(event && event.cartData);
      });
    }

    if (typeof window.fetch === 'function') {
      var origFetch = window.fetch;
      window.fetch = function (input) {
        var req = origFetch.apply(this, arguments);
        try {
          var href = typeof input === 'string' ? input : (input && input.url) || '';
          if (isCartUrl(href)) {
            req
              .then(function (res) {
                if (res && res.ok) setTimeout(syncCartAndPing, 250);
              })
              .catch(function () {});
          }
        } catch (e) {}
        return req;
      };
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__tgCartUrl = url;
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (xhr.__tgCartUrl && isCartUrl(xhr.__tgCartUrl)) {
          xhr.addEventListener('loadend', function () {
            if (xhr.status >= 200 && xhr.status < 300) setTimeout(syncCartAndPing, 250);
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  window.__tgViewerCountPing = function () {
    ping(false);
  };
  window.__tgViewerCountSetCart = function (inCart) {
    lastInCart = !!inCart;
    cartReadGen++;
    ping(false);
  };
  window.addEventListener('tg-viewer-count-refresh', function () {
    ping(false);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') start();
    else if (timer) {
      clearInterval(timer);
      timer = null;
    }
  });
  window.addEventListener('pagehide', function () {
    ping(true);
  });

  hookCartEvents();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
